import * as Sentry from '@sentry/react'
import {
  OAuthCancelledError,
  OAuthUnavailableError,
  type ProviderCredential,
} from './oauthTypes'

/**
 * Google Sign In on web via Google Identity Services.
 *
 * GIS does not expose an API to start the button flow from an async callback —
 * a real user gesture must reach a library-rendered button. We preload that
 * button while resolving availability, then on each sign-in re-initialize with
 * a fresh nonce and click it synchronously so the browser still has user
 * activation. `accounts.id` has no cancel callback (`error_callback` only
 * exists on the `accounts.oauth2` clients), so we watch the popup window
 * instead. A flow we cannot see waits for the timeout rather than guessing
 * from window focus: a late cancellation costs the user a spinner, while a
 * wrong one throws away a credential they already granted.
 */

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'
const POPUP_CLOSE_POLL_INTERVAL_MS = 250
const POPUP_OPEN_WATCH_MS = 2_000
const CREDENTIAL_ARRIVAL_GRACE_MS = 1_000
const SIGN_IN_TIMEOUT_MS = 90_000

type CredentialResponse = {
  credential: string
  select_by?: string
}

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: {
        callback: (response: CredentialResponse) => void
        client_id: string
        nonce?: string
        ux_mode?: 'popup' | 'redirect'
      }) => void
      renderButton: (
        parent: HTMLElement,
        options: {
          locale?: string
          shape?: string
          size?: string
          text?: string
          theme?: string
          type?: string
          width?: number
        },
      ) => void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

let scriptLoadPromise: Promise<void> | null = null
let preparePromise: Promise<void> | null = null
let buttonContainer: HTMLDivElement | null = null

function getGoogleWebClientId(): string {
  return (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined) ?? ''
}

function loadGoogleIdentityServices(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new OAuthUnavailableError(
        'Google Sign In requires a browser environment',
      ),
    )
  }
  if (window.google?.accounts?.id) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    ) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Identity Services')),
      )
      return
    }
    const script = document.createElement('script')
    script.src = GIS_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () =>
      reject(new Error('Failed to load Google Identity Services')),
    )
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

function generateRandomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function getGoogleAccountsId(): GoogleIdentityServices['accounts']['id'] {
  const accountsId = window.google?.accounts?.id
  if (!accountsId) {
    throw new OAuthUnavailableError(
      'Google Identity Services failed to initialise',
    )
  }
  return accountsId
}

function mountHiddenGoogleButton(): HTMLElement {
  buttonContainer?.remove()
  buttonContainer = document.createElement('div')
  buttonContainer.setAttribute('aria-hidden', 'true')
  buttonContainer.style.position = 'fixed'
  buttonContainer.style.left = '-9999px'
  buttonContainer.style.top = '0'
  document.body.appendChild(buttonContainer)

  getGoogleAccountsId().renderButton(buttonContainer, {
    size: 'large',
    text: 'continue_with',
    theme: 'outline',
    type: 'standard',
    width: 250,
  })

  const googleButton = buttonContainer.querySelector(
    'div[role="button"]',
  ) as HTMLElement | null
  if (!googleButton) {
    throw new OAuthUnavailableError('Google Sign In button failed to render')
  }
  return googleButton
}

/**
 * Warm the GIS script and hidden button so a later user tap can start sign-in
 * without awaiting network I/O (which would drop user activation).
 */
async function prepareGoogleSignIn(): Promise<void> {
  const googleWebClientId = getGoogleWebClientId()
  if (!preparePromise) {
    preparePromise = (async () => {
      await loadGoogleIdentityServices()
      getGoogleAccountsId().initialize({
        callback: () => {
          // Replaced per sign-in attempt; placeholder keeps GIS happy on warm-up.
        },
        client_id: googleWebClientId,
        ux_mode: 'popup',
      })
      mountHiddenGoogleButton()
    })().catch((error) => {
      preparePromise = null
      throw error
    })
  }
  await preparePromise
}

/**
 * Reports Google as available only once GIS can actually start a sign-in.
 * `signInWithGoogle` has to reach the rendered button within the click's user
 * activation, so a button offered while the script is still loading would turn
 * an early click into an error the user did nothing to cause.
 */
export async function isGoogleSignInAvailable(): Promise<boolean> {
  if (!getGoogleWebClientId()) return false
  if (typeof window === 'undefined') return false
  try {
    await prepareGoogleSignIn()
    return true
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

/**
 * Takes the handle of the popup GIS opens, by wrapping `window.open` around
 * the button click. GIS owns that window and never tells us when it closes, so
 * the handle is the only trustworthy way to tell a real cancellation from the
 * opener merely regaining focus while the user finishes signing in.
 *
 * The wrap outlives the click because GIS does not always open the window
 * synchronously, but it is lifted again shortly after so an unrelated
 * `window.open` elsewhere in the app is never mistaken for the sign-in popup.
 */
function watchWindowOpen({
  onBlocked,
  onOpened,
}: {
  onBlocked: () => void
  onOpened: (popup: Window) => void
}): { stop: () => void } {
  const originalWindowOpen = window.open
  window.open = (...openArguments: Parameters<typeof window.open>) => {
    const popup = originalWindowOpen.apply(window, openArguments)
    if (popup) onOpened(popup)
    else onBlocked()
    return popup
  }
  return {
    stop: () => {
      window.open = originalWindowOpen
    },
  }
}

export async function signInWithGoogle(): Promise<ProviderCredential> {
  const googleWebClientId = getGoogleWebClientId()

  if (!googleWebClientId) {
    throw new OAuthUnavailableError(
      'VITE_GOOGLE_WEB_CLIENT_ID is not configured for this funnel',
    )
  }
  if (typeof window === 'undefined') {
    throw new OAuthUnavailableError(
      'Google Sign In requires a browser environment',
    )
  }
  // Must not await here — callers invoke us under a user gesture, and
  // `isGoogleSignInAvailable` already waited for the script.
  if (!window.google?.accounts?.id) {
    throw new OAuthUnavailableError(
      'Google Identity Services is not loaded yet',
    )
  }

  const nonce = generateRandomString(32)
  const accountsId = getGoogleAccountsId()

  return new Promise<ProviderCredential>((resolve, reject) => {
    let settled = false
    let popupPollId: ReturnType<typeof setInterval> | null = null
    let cancelGraceId: ReturnType<typeof setTimeout> | null = null
    let popupWatch: { stop: () => void } | null = null
    let popupWatchEndId: ReturnType<typeof setTimeout> | null = null

    const stopPopupWatch = () => {
      if (popupWatchEndId !== null) {
        clearTimeout(popupWatchEndId)
        popupWatchEndId = null
      }
      popupWatch?.stop()
      popupWatch = null
    }

    const finish = (action: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      if (popupPollId !== null) clearInterval(popupPollId)
      if (cancelGraceId !== null) clearTimeout(cancelGraceId)
      stopPopupWatch()
      action()
    }

    const watchPopupUntilClosed = (popup: Window) => {
      stopPopupWatch()
      popupPollId = setInterval(() => {
        if (!popup.closed) return
        if (popupPollId !== null) {
          clearInterval(popupPollId)
          popupPollId = null
        }
        // GIS hands over the credential around the moment it closes the popup,
        // so let that land before calling this a cancellation.
        cancelGraceId = setTimeout(() => {
          finish(() => reject(new OAuthCancelledError()))
        }, CREDENTIAL_ARRIVAL_GRACE_MS)
      }, POPUP_CLOSE_POLL_INTERVAL_MS)
    }

    const timeoutId = setTimeout(() => {
      finish(() =>
        reject(
          new OAuthUnavailableError(
            'Google Sign In timed out. Please try again.',
          ),
        ),
      )
    }, SIGN_IN_TIMEOUT_MS)

    accountsId.initialize({
      callback: (response) => {
        finish(() => {
          if (!response.credential) {
            reject(
              new Error('Google Sign In did not return a credential token'),
            )
            return
          }
          resolve({ idToken: response.credential, nonce })
        })
      },
      client_id: googleWebClientId,
      nonce,
      ux_mode: 'popup',
    })

    let googleButton: HTMLElement
    try {
      googleButton = mountHiddenGoogleButton()
    } catch (error) {
      finish(() => {
        reject(
          error instanceof Error
            ? error
            : new OAuthUnavailableError(
                'Google Sign In button failed to render',
              ),
        )
      })
      return
    }

    popupWatch = watchWindowOpen({
      // The browser refused the window GIS asked for, so nothing will ever
      // call back. Say so now instead of leaving the button spinning.
      onBlocked: () =>
        finish(() =>
          reject(
            new OAuthUnavailableError(
              'Google Sign In popup was blocked by the browser',
            ),
          ),
        ),
      onOpened: watchPopupUntilClosed,
    })
    try {
      googleButton.click()
    } finally {
      // Nothing opened during the click, but GIS may still be getting there.
      popupWatchEndId = setTimeout(stopPopupWatch, POPUP_OPEN_WATCH_MS)
    }
  })
}
